import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { setForm((await api.get('/settings')).data); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !form) return <div className="p-8 text-center text-muted-foreground">Cargando…</div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.tax_rate = parseFloat(payload.tax_rate);
      payload.default_validity_days = parseInt(payload.default_validity_days);
      await api.put('/settings', payload);
      toast.success('Configuración guardada');
    } catch { toast.error('No se pudo guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Configuración"
        subtitle="Datos de tu empresa, impuestos y branding."
        actions={
          <Button className="btn-primary" onClick={save} disabled={saving} data-testid="settings-save-button">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Guardar
          </Button>
        }
      />
      <form onSubmit={save} className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="glass border-[var(--glass-border)]">
          <CardContent className="space-y-3 p-5">
            <h3 className="font-display text-base font-semibold">Datos de empresa</h3>
            <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.name || ''} onChange={set('name')} data-testid="settings-name-input" /></div>
            <div className="space-y-1.5"><Label>RIF</Label><Input value={form.rif || ''} onChange={set('rif')} data-testid="settings-rif-input" /></div>
            <div className="space-y-1.5"><Label>Dirección</Label><Textarea rows={2} value={form.address || ''} onChange={set('address')} data-testid="settings-address-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email || ''} onChange={set('email')} /></div>
              <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone || ''} onChange={set('phone')} /></div>
            </div>
            <div className="space-y-1.5"><Label>Sitio web</Label><Input value={form.website || ''} onChange={set('website')} /></div>
          </CardContent>
        </Card>

        <Card className="glass border-[var(--glass-border)]">
          <CardContent className="space-y-3 p-5">
            <h3 className="font-display text-base font-semibold">Fiscales</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Moneda</Label><Input value={form.currency || ''} onChange={set('currency')} data-testid="settings-currency-input" /></div>
              <div className="space-y-1.5"><Label>IVA (decimal)</Label><Input type="number" step="0.01" value={form.tax_rate ?? 0.16} onChange={set('tax_rate')} data-testid="settings-tax-input" /></div>
              <div className="space-y-1.5"><Label>Vigencia (días)</Label><Input type="number" value={form.default_validity_days ?? 15} onChange={set('default_validity_days')} data-testid="settings-validity-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notas por defecto</Label><Textarea rows={2} value={form.default_notes || ''} onChange={set('default_notes')} placeholder="Términos y condiciones que aparecerán por defecto en cada presupuesto…" /></div>
            <div className="space-y-1.5">
              <Label>Condiciones de pago por defecto</Label>
              <Textarea rows={3} value={form.default_payment_terms || ''} onChange={set('default_payment_terms')} placeholder="Ej: Precios en USD pagaderos a la tasa BCV. Si el pago es en divisas debe agregarse el IGTF pertinente." data-testid="settings-payment-terms-input" />
              <p className="text-[11px] text-muted-foreground">Texto que aparece en el pie del PDF y en la página pública. Editable por cada presupuesto desde el editor.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-[var(--glass-border)] lg:col-span-2">
          <CardContent className="space-y-3 p-5">
            <h3 className="font-display text-base font-semibold">Branding</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5"><Label>Logo (fondo claro / PDF)</Label><Input value={form.logo_light_url || ''} onChange={set('logo_light_url')} /></div>
              <div className="space-y-1.5"><Label>Logo (fondo oscuro / UI)</Label><Input value={form.logo_dark_url || ''} onChange={set('logo_dark_url')} /></div>
              <div className="space-y-1.5"><Label>Isologo</Label><Input value={form.isologo_url || ''} onChange={set('isologo_url')} /></div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[form.logo_light_url, form.logo_dark_url, form.isologo_url].map((u, i) => (
                <div key={i} className="flex h-28 items-center justify-center rounded-lg bg-[var(--surface-1)] p-2">
                  {u ? <img src={u} alt="logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground">Sin imagen</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
